
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '@/hooks/use-toast';
import { SEOSettings } from '@/components/admin/seo/types';

export const useSEOSettings = () => {
  const [settings, setSettings] = useState<SEOSettings>({
    title: '',
    description: '',
    keywords: '',
    google_analytics_id: '',
    google_site_verification: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchSEOSettings();
  }, []);

  const fetchSEOSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('UI: Fetching SEO settings from API');
      
      const response = await axios.get('/api/seo-settings', {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      console.log('UI: SEO settings API response:', response.data);
      
      if (response.data) {
        setSettings(response.data);
        console.log('UI: SEO settings loaded successfully');
      } else {
        console.log('UI: No SEO settings found. Using defaults.');
        setError('No SEO settings found in the database. Default values will be used.');
      }
    } catch (error) {
      console.error('UI: Error fetching SEO settings:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load SEO settings. Please try again later.';
      setError(errorMessage);
      toast({
        title: "Error",
        description: "Failed to load SEO settings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear success/error messages when user starts editing
    setSuccess(null);
    setError(null);
  };

  const updateSEOSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      console.log('UI: Sending SEO settings update to API:', settings);
      
      // Validate required fields
      if (!settings.title) {
        setError('Title is required');
        setLoading(false);
        return;
      }
      
      const response = await axios.post('/api/seo-settings', settings, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      console.log('UI: SEO settings update API response:', response.data);
      
      if (response.data && response.data.id) {
        setSuccess('SEO settings updated successfully!');
        setSettings(response.data);
        toast({
          title: "Success",
          description: "SEO settings updated successfully",
        });
      } else {
        throw new Error('Server returned an invalid response');
      }
    } catch (error) {
      console.error('UI: Error updating SEO settings:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update SEO settings. Please try again.';
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateMetaTags = () => {
    // Update meta tags in real-time when in development
    const descriptionTag = document.getElementById('meta-description');
    if (descriptionTag) {
      descriptionTag.setAttribute('content', settings.description);
    }
    
    const keywordsTag = document.getElementById('meta-keywords');
    if (keywordsTag) {
      keywordsTag.setAttribute('content', settings.keywords);
    }
    
    const verificationTag = document.getElementById('google-site-verification');
    if (verificationTag) {
      verificationTag.setAttribute('content', settings.google_site_verification);
    }
    
    // Update document title
    document.title = settings.title;
  };

  // Call updateMetaTags whenever settings change
  useEffect(() => {
    updateMetaTags();
  }, [settings]);

  return {
    settings,
    loading,
    error,
    success,
    handleInputChange,
    updateSEOSettings,
  };
};
